/**
 * @file Guide list (Guide → Chapter → Page). Create, edit, delete, reorder; link to chapters under each guide.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canManageContent, isAdmin } from "@/lib/permissions";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { slugify } from "@/lib/shared";

interface GuideRow {
  id: string;
  title: string;
  slug: string;
  slugAutoFromTitle: boolean;
  sortOrder: number;
  _count: { chapters: number };
}

function SortableGuideRow({
  guide,
  onEdit,
  onRemove,
  canDelete,
}: {
  guide: GuideRow;
  onEdit: (c: GuideRow) => void;
  onRemove: (c: GuideRow) => void;
  canDelete: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: guide.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell
        sx={{
          width: 48,
          pr: 0,
          verticalAlign: "middle",
        }}
      >
        <Tooltip title="Drag to reorder">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab", touchAction: "none" }}
            aria-label="Drag to reorder guide"
          >
            <DragHandleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ fontWeight: 600 }}>
        <Link
          to={`/dashboard/chapters?guideId=${guide.id}`}
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {guide.title}
        </Link>
      </TableCell>
      <TableCell sx={{ color: "text.secondary" }}>{guide.slug}</TableCell>
      <TableCell>{guide.sortOrder + 1}</TableCell>
      <TableCell>{guide._count.chapters}</TableCell>
      <TableCell align="right">
        <Tooltip title="Edit guide">
          <IconButton size="small" color="primary" onClick={() => onEdit(guide)} aria-label="Edit guide">
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canDelete ? (
          <Tooltip title="Delete guide">
            <IconButton size="small" color="error" onClick={() => void onRemove(guide)} aria-label="Delete guide">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function GuidesPage() {
  const { user } = useAuth();
  const canEdit = useMemo(() => canManageContent(user?.roles), [user?.roles]);
  const canDelete = useMemo(() => isAdmin(user?.roles), [user?.roles]);

  const [rows, setRows] = useState<GuideRow[]>([]);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<GuideRow | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editSlugManual, setEditSlugManual] = useState(false);
  const [reorderPending, setReorderPending] = useState(false);
  const [guideToDelete, setGuideToDelete] = useState<GuideRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = useCallback(async () => {
    const data = await apiFetch<GuideRow[]>("/api/v1/guides");
    setRows(data);
  }, []);

  useEffect(() => {
    void load().catch((e) => setError(String(e)));
  }, [load]);

  function openCreateDialog() {
    setTitle("");
    setSlug("");
    setSlugManual(false);
    setCreateOpen(true);
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    await apiFetch("/api/v1/guides", {
      method: "POST",
      json: { title, ...(slugManual ? { slug } : {}) },
    });
    setTitle("");
    setSlug("");
    setSlugManual(false);
    setCreateOpen(false);
    await load();
  }

  function openEdit(c: GuideRow) {
    setEditing(c);
    setEditTitle(c.title);
    setEditSlug(c.slug);
    setEditSlugManual(!c.slugAutoFromTitle);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) {
      return;
    }
    setError(null);
    await apiFetch(`/api/v1/guides/${editing.id}`, {
      method: "PATCH",
      json: { title: editTitle, slug: editSlug },
    });
    setEditing(null);
    await load();
  }

  function requestDeleteGuide(c: GuideRow) {
    setGuideToDelete(c);
  }

  async function confirmDeleteGuide() {
    if (!guideToDelete) {
      return;
    }
    const c = guideToDelete;
    setGuideToDelete(null);
    setError(null);
    try {
      await apiFetch(`/api/v1/guides/${c.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(String(e));
    }
  }

  const deleteGuideMessage = guideToDelete
    ? guideToDelete._count.chapters > 0
      ? `This guide has ${guideToDelete._count.chapters} chapter(s). Deleting it will also remove those chapters and pages from the CMS and help site. Continue?`
      : `Delete guide “${guideToDelete.title}”?`
    : "";

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    const reordered = arrayMove(rows, oldIndex, newIndex);
    setRows(reordered);
    setReorderPending(true);
    setError(null);
    try {
      const updated = await apiFetch<GuideRow[]>("/api/v1/guides/reorder", {
        method: "POST",
        json: { guideIds: reordered.map((r) => r.id) },
      });
      setRows(updated);
    } catch (e) {
      setError(String(e));
      await load();
    } finally {
      setReorderPending(false);
    }
  }

  const sortableIds = rows.map((r) => r.id);

  const tableContent = (
    <Table size="medium">
      <TableHead>
        <TableRow sx={{ bgcolor: "action.hover" }}>
          {canEdit ? <TableCell width={48} aria-label="Reorder" /> : null}
          <TableCell>Title</TableCell>
          <TableCell>Slug</TableCell>
          <TableCell width={100}>Sort order</TableCell>
          <TableCell>Chapters</TableCell>
          {canEdit ? (
            <TableCell align="right" width={120}>
              Actions
            </TableCell>
          ) : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {canEdit ? (
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {rows.map((c) => (
              <SortableGuideRow
                key={c.id}
                guide={c}
                onEdit={openEdit}
                onRemove={requestDeleteGuide}
                canDelete={canDelete}
              />
            ))}
          </SortableContext>
        ) : (
          rows.map((c) => (
            <TableRow key={c.id} hover>
              <TableCell sx={{ fontWeight: 600 }}>
                <Link to={`/dashboard/chapters?guideId=${c.id}`} style={{ color: "inherit", textDecoration: "none" }}>
                  {c.title}
                </Link>
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{c.slug}</TableCell>
              <TableCell>{c.sortOrder + 1}</TableCell>
              <TableCell>{c._count.chapters}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight={700}>
          Guides
        </Typography>
        {canEdit ? (
          <Button variant="contained" onClick={openCreateDialog}>
            Add guide
          </Button>
        ) : null}
      </Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {canEdit ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <TableContainer
            component={Paper}
            sx={{
              border: 1,
              borderColor: "divider",
              opacity: reorderPending ? 0.7 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {tableContent}
          </TableContainer>
        </DndContext>
      ) : (
        <TableContainer component={Paper} sx={{ border: 1, borderColor: "divider" }}>
          {tableContent}
        </TableContainer>
      )}

      <ConfirmDialog
        open={guideToDelete !== null}
        title="Delete guide?"
        message={deleteGuideMessage}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={() => void confirmDeleteGuide()}
        onClose={() => setGuideToDelete(null)}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add guide</DialogTitle>
        <Box component="form" onSubmit={create}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              autoFocus
              value={title}
              onChange={(e) => {
                const v = e.target.value;
                setTitle(v);
                if (!slugManual) {
                  setSlug(slugify(v));
                }
              }}
            />
            <TextField
              label="Slug"
              fullWidth
              required
              value={slug}
              onChange={(e) => {
                setSlugManual(true);
                setSlug(e.target.value);
              }}
              helperText="Filled from title until you edit it."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Add guide
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit guide</DialogTitle>
        <Box component="form" onSubmit={saveEdit}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={editTitle}
              onChange={(e) => {
                const v = e.target.value;
                setEditTitle(v);
                if (!editSlugManual) {
                  setEditSlug(slugify(v));
                }
              }}
            />
            <TextField
              label="Slug"
              fullWidth
              required
              value={editSlug}
              onChange={(e) => {
                setEditSlugManual(true);
                setEditSlug(e.target.value);
              }}
              helperText="Stays in sync with title until you edit the slug."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
